/**
 * Órdenes — el trabajo antes de la factura.
 *
 * Qué datos pide cada orden y por qué pasos va lo define su tipo de orden
 * (Configuración → Tipos de orden). Esta pantalla no sabe de carros ni de
 * cortes de pelo: dibuja los campos del tipo y mueve la orden por sus pasos.
 *
 * Es la pantalla del mostrador: se abre muchas veces al día, así que abrir
 * una orden es el camino más corto posible y el paso se cambia desde la
 * lista, sin abrir nada.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Table, TableHead, TableRow,
  TableCell, TableBody, TableContainer, Paper, Chip, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Stack,
  Alert, CircularProgress, Tooltip, Divider, Autocomplete, Tabs, Tab,
  FormControlLabel, Switch, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BlockIcon from '@mui/icons-material/Block';
import SearchIcon from '@mui/icons-material/Search';
import UndoIcon from '@mui/icons-material/Undo';
import PrintIcon from '@mui/icons-material/Print';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import ViewListIcon from '@mui/icons-material/ViewList';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useSede } from '../contexto/Sede';
import { compacto } from './Tile';
import SelectorCliente from './SelectorCliente';
import { CampoTelefono } from './CamposContacto';
import { errorTelefono } from '../contacto';
import { useEsMovil, tablaAncha, filtro, ENCABEZADO, botonEncabezado, rotulo } from '../estilos/responsivo';
import { TARJETA, TARJETA_INTERACTIVA, ETIQUETA_BLOQUE } from '../estilos/tarjeta';

const fmt = (v) => '$' + new Intl.NumberFormat('es-CO',
  { maximumFractionDigits: 0 }).format(Number(v || 0));
const fechaHora = (v) => (v ? new Date(v).toLocaleString('es-CO',
  { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const fechaCorta = (v) => (v ? new Date(v).toLocaleDateString('es-CO',
  { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

// El color sale de la fase, que es lo que el sistema entiende; el nombre, del paso.
const COLOR_FASE = {
  abierta: { bg: '#e0f2fe', fg: '#0369a1' },
  en_proceso: { bg: '#fef3c7', fg: '#a16207' },
  terminada: { bg: '#dcfce7', fg: '#15803d' },
  facturada: { bg: '#ede9fe', fg: '#5b21b6' },
  anulada: { bg: '#fee2e2', fg: '#b91c1c' },
};

const LINEA_VACIA = { servicio_id: '', concepto: '', cantidad: 1, precio_unitario: '' };

const valorTexto = (campo, v) => {
  if (v === null || v === undefined || v === '') return '';
  if (campo.tipo === 'si_no') return v ? 'Sí' : 'No';
  if (campo.tipo === 'fecha') return fechaCorta(v + 'T12:00:00');
  return String(v);
};

// Lo que se ve de una orden en la lista: el identificador grande y, al lado,
// los otros datos cortos (no los textos largos).
const resumenDatos = (tipo, datos) => {
  if (!tipo) return { principal: '', resto: '' };
  const ident = tipo.campos.find(c => c.identificador);
  const otros = tipo.campos.filter(c => !c.identificador && c.tipo !== 'texto_largo')
    .map(c => valorTexto(c, datos?.[c.clave])).filter(Boolean);
  return { principal: ident ? valorTexto(ident, datos?.[ident.clave]) : '',
           resto: otros.slice(0, 4).join(' · ') };
};

const escapar = (t) => String(t ?? '').replace(/[&<>"]/g,
  (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

export default function OrdenesServicio() {
  const esMovil = useEsMovil();
  const navigate = useNavigate();
  const { sedes, sedeId, esTodas, nombre: nombreSede, params: paramsSede } = useSede();
  const [tipos, setTipos] = useState(null);
  const [tipoId, setTipoId] = useState(null);
  const [ordenes, setOrdenes] = useState([]);
  const [terceros, setTerceros] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [estadoFiltro, setEstadoFiltro] = useState('abiertas');
  const [buscar, setBuscar] = useState('');
  // Tablero (kanban por pasos) o lista. Se recuerda en este navegador.
  const [vista, setVista] = useState(() => {
    try { return localStorage.getItem('erp.ordenes.vista') || 'tablero'; } catch { return 'tablero'; }
  });
  const cambiarVista = (v) => {
    if (!v) return;
    setVista(v);
    try { localStorage.setItem('erp.ordenes.vista', v); } catch { /* sin almacenamiento */ }
  };
  const [arrastrando, setArrastrando] = useState(null);
  const [sobre, setSobre] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [edicion, setEdicion] = useState(null);
  const [errorForm, setErrorForm] = useState('');
  const [anulando, setAnulando] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [sugeridos, setSugeridos] = useState([]);
  const [historial, setHistorial] = useState(null);
  const [emisor, setEmisor] = useState(null);

  useEffect(() => {
    api.get('/tipos-orden').then(r => {
      const t = r.data || [];
      setTipos(t);
      setTipoId(id => (t.find(x => x.id === id) ? id : (t[0] ? t[0].id : null)));
    }).catch(e => { setTipos([]); setError(e.response?.data?.detail || 'No se pudieron cargar los tipos de orden'); });
    api.get('/configuracion/emisor').then(r => setEmisor(r.data)).catch(() => {});
  }, []);
  const tipo = useMemo(() => (tipos || []).find(t => t.id === tipoId) || null, [tipos, tipoId]);
  const tipoDe = (o) => (tipos || []).find(t => t.id === o.tipo_orden_id) || tipo;
  const campoId = tipo ? tipo.campos.find(c => c.identificador) : null;

  const cargar = useCallback(async () => {
    if (!tipoId) { setLoading(false); return; }
    setLoading(true);
    try {
      const params = { ...paramsSede, tipo_orden_id: tipoId };
      // El tablero muestra solo lo que está en curso: lo facturado y lo
      // anulado ya no se mueve, y se consulta en la lista.
      const estado = vista === 'tablero' ? 'abiertas' : estadoFiltro;
      if (estado) params.estado = estado;
      if (buscar.trim()) params.buscar = buscar.trim();
      const [o, r] = await Promise.all([
        api.get('/ordenes', { params }),
        api.get('/ordenes/tablero/resumen', { params: { ...paramsSede, tipo_orden_id: tipoId } }),
      ]);
      setOrdenes(o.data); setResumen(r.data); setError('');
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudieron cargar las órdenes');
    } finally { setLoading(false); }
  }, [paramsSede, estadoFiltro, buscar, tipoId, vista]);
  useEffect(() => { cargar(); }, [cargar]);

  const cargarTerceros = useCallback(() => api.get('/terceros')
    .then(r => setTerceros(r.data.filter(t => t.es_cliente))).catch(() => {}), []);
  useEffect(() => { cargarTerceros(); }, [cargarTerceros]);
  // El catálogo y los empleados dependen de la sede: hay servicios que no se
  // ofrecen en todas y empleados que trabajan en una sola.
  useEffect(() => {
    api.get('/servicios', { params: paramsSede })
      .then(r => setCatalogo(r.data || [])).catch(() => {});
    api.get('/empleados', { params: paramsSede })
      .then(r => setEmpleados(r.data || [])).catch(() => {});
  }, [paramsSede]);

  const vendibles = catalogo.filter(s => (tipo?.permite_productos ? true : (s.tipo || 'servicio') === 'servicio'));
  const palabraLineas = tipo?.permite_productos ? 'productos y servicios' : 'servicios';

  const totalLineas = (lineas) => (lineas || []).reduce(
    (s, l) => s + (Number(l.cantidad || 0) * Number(l.precio_unitario || 0)), 0);

  const nueva = () => {
    setEdicion({ sede_id: sedeId || '', tipo_orden_id: tipoId, datos: {},
      tercero_id: '', cliente_nombre: '', cliente_telefono: '', empleado_id: '',
      responsable: '', impuesto_pct: 19, observaciones: '', lineas: [{ ...LINEA_VACIA }] });
    setErrorForm(''); setHistorial(null); setSugeridos([]);
  };

  const setDato = (clave, valor) => setEdicion(e => ({ ...e, datos: { ...e.datos, [clave]: valor } }));

  // Lo escrito en el identificador se busca entre lo ya atendido.
  const textoId = edicion && campoId ? String(edicion.datos?.[campoId.clave] || '').trim() : '';
  useEffect(() => {
    if (!textoId || (edicion && edicion.id)) { setSugeridos([]); return undefined; }
    const h = setTimeout(() => {
      api.get('/ordenes/identificadores', { params: { tipo_orden_id: tipoId, buscar: textoId } })
        .then(r => setSugeridos(r.data || [])).catch(() => {});
    }, 250);
    return () => clearTimeout(h);
  }, [textoId, tipoId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Si ya vino antes, se trae lo de la última vez: los datos que falten y el
  // cliente. Es lo que ahorra tiempo con el cliente esperando.
  const traerHistorial = async (valor) => {
    if (!campoId || !String(valor || '').trim()) { setHistorial(null); return; }
    try {
      const { data } = await api.get('/ordenes/historial',
        { params: { tipo_orden_id: tipoId, identificador: valor } });
      setHistorial(data.visitas ? data : null);
      if (data.ultima) {
        const u = data.ultima;
        setEdicion(e => {
          const datos = { ...e.datos };
          tipo.campos.forEach(c => {
            if (!c.identificador && (datos[c.clave] === undefined || datos[c.clave] === '')
                && u.datos?.[c.clave] !== undefined) datos[c.clave] = u.datos[c.clave];
          });
          return { ...e, datos,
            tercero_id: e.tercero_id || u.tercero_id || '',
            cliente_nombre: e.cliente_nombre || (u.tercero_id ? u.cliente : ''),
            cliente_telefono: e.cliente_telefono || u.cliente_telefono || '' };
        });
      }
    } catch { setHistorial(null); }
  };

  const guardar = async () => {
    const cuerpo = {
      sede_id: Number(edicion.sede_id),
      tipo_orden_id: edicion.tipo_orden_id,
      datos: edicion.datos || {},
      tercero_id: edicion.tercero_id || null,
      cliente_nombre: edicion.cliente_nombre || null,
      cliente_telefono: edicion.cliente_telefono || null,
      empleado_id: edicion.empleado_id || null,
      responsable: edicion.responsable || null,
      impuesto_pct: Number(edicion.impuesto_pct || 0),
      observaciones: edicion.observaciones || null,
      lineas: (edicion.lineas || [])
        .filter(l => l.concepto && Number(l.precio_unitario) > 0)
        .map(l => ({ servicio_id: l.servicio_id || null,
                     concepto: l.concepto, descripcion: l.descripcion || null,
                     cantidad: Number(l.cantidad || 1),
                     precio_unitario: Number(l.precio_unitario || 0) })),
    };
    if (!cuerpo.sede_id) { setErrorForm('Escoge la sede.'); return; }
    if (errorTelefono(edicion.cliente_telefono)) { setErrorForm(errorTelefono(edicion.cliente_telefono)); return; }
    if (!cuerpo.tercero_id) {
      setErrorForm('Falta el cliente. Escríbelo en el campo Cliente: si no está '
        + 'registrado, la lista ofrece crearlo ahí mismo.');
      return;
    }
    if (!cuerpo.lineas.length) { setErrorForm(`Agrega al menos un ítem de ${palabraLineas} con su precio.`); return; }
    try {
      if (edicion.id) await api.put(`/ordenes/${edicion.id}`, cuerpo);
      else await api.post('/ordenes', cuerpo);
      setEdicion(null); setErrorForm(''); cargar();
    } catch (e) { setErrorForm(e.response?.data?.detail || 'No se pudo guardar'); }
  };

  const moverA = async (o, cambio) => {
    try { await api.patch(`/ordenes/${o.id}/estado`, cambio); cargar(); }
    catch (e) { setError(e.response?.data?.detail || 'No se pudo cambiar el paso'); }
  };

  const facturar = async (o) => {
    if (!window.confirm(`¿Facturar la orden ${o.numero} por ${fmt(o.total)}?`)) return;
    try {
      const { data } = await api.post(`/ordenes/${o.id}/facturar`);
      setAviso(`Factura ${data.numero_factura} generada por ${fmt(data.total)}`);
      cargar();
    } catch (e) { setError(e.response?.data?.detail || 'No se pudo facturar'); }
  };

  const borrar = async (o) => {
    if (!window.confirm(`¿Borrar la orden ${o.numero}?`)) return;
    try { await api.delete(`/ordenes/${o.id}`); cargar(); }
    catch (e) { setError(e.response?.data?.detail || 'No se pudo borrar'); }
  };

  /** El comprobante para el cliente: datos de la orden y el texto del tipo. */
  const imprimir = (o) => {
    const t = tipoDe(o);
    const filas = (t?.campos || []).map(c => [c.nombre, valorTexto(c, o.datos?.[c.clave])])
      .filter(([, v]) => v);
    const html = `<!doctype html><html lang="es"><head><meta charset="utf-8">
<title>${escapar(o.numero)}</title><style>
body{font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;color:#0f172a;margin:24px;font-size:13px}
h1{font-size:18px;margin:0}h2{font-size:14px;margin:18px 0 6px}.g{color:#64748b}
table{width:100%;border-collapse:collapse}td,th{padding:5px 4px;border-bottom:1px solid #e2e8f0;text-align:left}
.d{text-align:right}.t{font-size:16px;font-weight:800}.pie{margin-top:22px;font-size:12px;color:#334155;white-space:pre-wrap}
</style></head><body>
<div style="display:flex;justify-content:space-between;gap:16px"><div>
<h1>${escapar(emisor?.razon_social || '')}</h1><div class="g">${escapar(emisor?.nit ? 'NIT ' + emisor.nit : '')}
${escapar(o.sede_nombre || '')}</div></div><div style="text-align:right"><div class="g">${escapar(t?.nombre || 'Orden')}</div>
<h1>${escapar(o.numero)}</h1><div class="g">${escapar(fechaHora(o.fecha_ingreso))}</div></div></div>
<h2>Cliente</h2><div>${escapar(o.cliente)}${o.cliente_telefono ? ' · ' + escapar(o.cliente_telefono) : ''}</div>
${o.responsable ? `<div class="g">Atiende: ${escapar(o.responsable)}</div>` : ''}
${filas.length ? `<h2>Datos</h2><table>${filas.map(([k, v]) =>
  `<tr><td class="g" style="width:35%">${escapar(k)}</td><td>${escapar(v)}</td></tr>`).join('')}</table>` : ''}
<h2>Detalle</h2><table><tr><th>Concepto</th><th class="d">Cant.</th><th class="d">Precio</th><th class="d">Subtotal</th></tr>
${o.lineas.map(l => `<tr><td>${escapar(l.concepto)}</td><td class="d">${escapar(l.cantidad)}</td>
<td class="d">${fmt(l.precio_unitario)}</td><td class="d">${fmt(l.subtotal)}</td></tr>`).join('')}
<tr><td colspan="3" class="d g">Subtotal</td><td class="d">${fmt(o.subtotal)}</td></tr>
<tr><td colspan="3" class="d g">IVA ${escapar(o.impuesto_pct)}%</td><td class="d">${fmt(o.impuesto_monto)}</td></tr>
<tr><td colspan="3" class="d t">Total</td><td class="d t">${fmt(o.total)}</td></tr></table>
${o.observaciones ? `<h2>Observaciones</h2><div>${escapar(o.observaciones)}</div>` : ''}
${t?.texto_comprobante ? `<div class="pie">${escapar(t.texto_comprobante)}</div>` : ''}
<script>window.onload=function(){window.print()}</script></body></html>`;
    const w = window.open('', '_blank');
    if (!w) { setError('El navegador bloqueó la ventana del comprobante. Permite ventanas emergentes.'); return; }
    w.document.write(html); w.document.close();
  };

  const abrirEdicion = (o) => {
    setEdicion({ ...o, datos: { ...(o.datos || {}) }, tercero_id: o.tercero_id || '',
      empleado_id: o.empleado_id || '',
      lineas: o.lineas.length ? o.lineas : [{ ...LINEA_VACIA }] });
    setErrorForm(''); setHistorial(null);
  };

  /**
   * Los botones de una orden. Viven en una función porque la tabla del
   * escritorio y las tarjetas del teléfono muestran exactamente los mismos.
   */
  const acciones = (o) => {
    const t = tipoDe(o);
    const pasos = t?.pasos || [];
    const i = pasos.findIndex(p => p.clave === o.paso);
    const vivo = !['facturada', 'anulada'].includes(o.estado);
    const siguiente = vivo && i >= 0 ? pasos[i + 1] : null;
    const anterior = vivo && i > 0 ? pasos[i - 1] : null;
    return (
      <Stack direction="row" spacing={0.25} justifyContent="flex-end" flexWrap="wrap">
        {siguiente && (
          <Tooltip title={`Pasar a «${siguiente.nombre}»`}>
            <IconButton size="small" color="success" onClick={() => moverA(o, { paso: siguiente.clave })}>
              <ArrowForwardIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {o.estado === 'terminada' && (
          <Tooltip title="Facturar esta orden">
            <IconButton size="small" color="primary" onClick={() => facturar(o)}>
              <ReceiptIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {/* Ya facturada, el mismo ícono abre la factura que existe.
            Volver a generarla sería facturarle dos veces al cliente. */}
        {o.estado === 'facturada' && (
          <Tooltip title={o.factura_id ? `Ver la factura ${o.numero_factura || ''}`.trim()
            : 'Esta orden quedó marcada como facturada pero no tiene factura enlazada'}>
            <span>
              <IconButton size="small" color="primary" disabled={!o.factura_id}
                onClick={() => navigate(`/facturas/${o.factura_id}`)}>
                <ReceiptIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {anterior && (
          <Tooltip title={`Devolver a «${anterior.nombre}»`}>
            <IconButton size="small" onClick={() => moverA(o, { paso: anterior.clave })}>
              <UndoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Imprimir comprobante">
          <IconButton size="small" onClick={() => imprimir(o)}><PrintIcon fontSize="small" /></IconButton>
        </Tooltip>
        {['abierta', 'en_proceso'].includes(o.estado) && (
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => abrirEdicion(o)}><EditIcon fontSize="small" /></IconButton>
          </Tooltip>
        )}
        {vivo && (
          <>
            <Tooltip title="Anular">
              <IconButton size="small" color="error" onClick={() => { setAnulando(o); setMotivo(''); }}>
                <BlockIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Borrar">
              <IconButton size="small" color="error" onClick={() => borrar(o)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Stack>
    );
  };

  const chipPaso = (o) => {
    const c = COLOR_FASE[o.estado] || { bg: '#f1f5f9', fg: '#475569' };
    const label = o.estado === 'facturada' ? 'Facturada' : o.estado === 'anulada' ? 'Anulada'
      : (o.paso_nombre || o.estado);
    return <Chip size="small" label={label} sx={{ bgcolor: c.bg, color: c.fg, fontSize: 11, height: 22 }} />;
  };

  // Un campo del tipo de orden, según su clase.
  const campoFormulario = (c) => {
    const v = edicion.datos?.[c.clave];
    const etiqueta = c.nombre + (c.obligatorio ? ' *' : '');
    if (c.identificador && !edicion.id) {
      return (
        <Autocomplete key={c.clave} freeSolo autoHighlight fullWidth size="small"
          options={sugeridos.map(s => s.identificador)}
          filterOptions={(ops) => ops}
          inputValue={v === undefined || v === null ? '' : String(v)}
          onInputChange={(_, texto, razon) => {
            if (razon === 'reset') return;
            setDato(c.clave, c.tipo === 'texto' ? (texto || '').toUpperCase() : texto);
          }}
          onChange={(_, valor) => { if (valor) { setDato(c.clave, valor); traerHistorial(valor); } }}
          renderOption={(props, op) => {
            const s = sugeridos.find(x => x.identificador === op);
            return (
              <li {...props} key={op}>
                <Box sx={{ display: 'flex', width: '100%', gap: 2, alignItems: 'baseline' }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 800, flexGrow: 1,
                                    fontFamily: 'ui-monospace, monospace' }}>{op}</Typography>
                  <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                    {s ? `${s.visitas} vez${s.visitas === 1 ? '' : 'es'}` : ''}
                  </Typography>
                </Box>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField {...params} label={etiqueta}
              onBlur={() => traerHistorial(v)}
              helperText={historial ? `Ya vino ${historial.visitas} vez${historial.visitas === 1 ? '' : 'es'}`
                : 'Si ya vino, se traen sus datos'}
              inputProps={{ ...params.inputProps, style: { fontWeight: 700 } }} />
          )} />
      );
    }
    if (c.tipo === 'lista') {
      return (
        <TextField key={c.clave} select size="small" fullWidth label={etiqueta} value={v || ''}
          onChange={e => setDato(c.clave, e.target.value)}>
          {!c.obligatorio && <MenuItem value="">—</MenuItem>}
          {c.opciones.map(op => <MenuItem key={op} value={op}>{op}</MenuItem>)}
        </TextField>
      );
    }
    if (c.tipo === 'si_no') {
      return (
        <FormControlLabel key={c.clave} sx={{ ml: 0 }}
          control={<Switch checked={Boolean(v)} onChange={e => setDato(c.clave, e.target.checked)} />}
          label={c.nombre} />
      );
    }
    return (
      <TextField key={c.clave} size="small" fullWidth label={etiqueta}
        type={c.tipo === 'numero' ? 'number' : c.tipo === 'fecha' ? 'date' : 'text'}
        multiline={c.tipo === 'texto_largo'} minRows={c.tipo === 'texto_largo' ? 2 : undefined}
        InputLabelProps={c.tipo === 'fecha' ? { shrink: true } : undefined}
        value={v === undefined || v === null ? '' : v}
        onChange={e => setDato(c.clave, e.target.value)}
        sx={c.tipo === 'texto_largo' ? { gridColumn: '1 / -1' } : undefined} />
    );
  };

  /**
   * El tablero: una columna por paso del tipo de orden. Se arrastra una
   * tarjeta a otra columna para cambiarla de paso; en el teléfono, donde
   * arrastrar no sirve, las flechas de cada tarjeta hacen lo mismo.
   */
  const tablero = () => {
    const pasos = tipo?.pasos || [];
    const soltar = (paso) => {
      const o = ordenes.find(x => x.id === arrastrando);
      setArrastrando(null); setSobre(null);
      if (!o || o.paso === paso.clave) return;
      // Se mueve en pantalla de una vez; si el servidor no lo acepta, la
      // recarga lo devuelve a su sitio y aparece el motivo.
      setOrdenes(os => os.map(x => (x.id === o.id
        ? { ...x, paso: paso.clave, paso_nombre: paso.nombre, estado: paso.fase } : x)));
      moverA(o, { paso: paso.clave });
    };
    return (
      <Box sx={{ display: 'flex', gap: 1, p: TARJETA.relleno, overflowX: 'auto', alignItems: 'flex-start',
                 minHeight: 320 }}>
        {pasos.map(p => {
          const enPaso = ordenes.filter(o => o.paso === p.clave);
          const color = COLOR_FASE[p.fase] || { bg: '#f1f5f9', fg: '#475569' };
          return (
            <Box key={p.clave}
              onDragOver={(e) => { if (arrastrando) { e.preventDefault(); setSobre(p.clave); } }}
              onDragLeave={() => setSobre(s => (s === p.clave ? null : s))}
              onDrop={(e) => { e.preventDefault(); soltar(p); }}
              sx={{ flex: { xs: '0 0 82%', sm: '0 0 280px' }, bgcolor: sobre === p.clave ? color.bg : '#f8fafc',
                    border: '1px solid', borderColor: sobre === p.clave ? color.fg : '#e2e8f0',
                    borderRadius: TARJETA.radio, p: 1, transition: 'background-color .15s' }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 0.5, pb: 0.75 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color.fg }} />
                <Typography sx={{ fontWeight: 800, fontSize: 13.5, flexGrow: 1 }}>{p.nombre}</Typography>
                <Chip size="small" label={enPaso.length} sx={{ height: 20, fontSize: 11 }} />
              </Stack>
              <Typography sx={{ fontSize: 11, color: 'text.secondary', px: 0.5, mt: -0.5, mb: 0.75 }}>
                {fmt(enPaso.reduce((t, o) => t + (o.total || 0), 0))}
                {p.fase === 'terminada' ? ' · lista para facturar' : ''}
              </Typography>
              <Stack spacing={0.75}>
                {enPaso.map(o => {
                  const r = resumenDatos(tipoDe(o), o.datos);
                  return (
                    <Paper key={o.id} elevation={0} draggable={!esMovil}
                      onDragStart={(e) => { setArrastrando(o.id); e.dataTransfer.effectAllowed = 'move'; }}
                      onDragEnd={() => { setArrastrando(null); setSobre(null); }}
                      sx={{ ...TARJETA_INTERACTIVA, cursor: esMovil ? 'default' : 'grab',
                            opacity: arrastrando === o.id ? 0.5 : 1 }}>
                      <Stack direction="row" alignItems="baseline" spacing={1}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 800, flexGrow: 1, minWidth: 0,
                                          fontFamily: r.principal ? 'ui-monospace, monospace' : undefined }}
                          noWrap>
                          {r.principal || o.cliente}
                        </Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 800, whiteSpace: 'nowrap' }}>
                          {fmt(o.total)}
                        </Typography>
                      </Stack>
                      {r.resto && (
                        <Typography noWrap sx={{ fontSize: 11.5, color: 'text.secondary' }}>{r.resto}</Typography>
                      )}
                      {r.principal && <Typography noWrap sx={{ fontSize: 12.5 }}>{o.cliente}</Typography>}
                      <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>
                        {o.numero} · {fechaHora(o.fecha_ingreso)}
                        {o.responsable ? ` · ${o.responsable}` : ''}
                      </Typography>
                      <Box sx={{ mt: 0.5, pt: 0.25, borderTop: '1px solid rgba(15,23,42,.08)' }}>
                        {acciones(o)}
                      </Box>
                    </Paper>
                  );
                })}
                {enPaso.length === 0 && (
                  <Typography sx={{ fontSize: 12, color: 'text.disabled', textAlign: 'center', py: 2 }}>
                    {esMovil ? 'Sin órdenes' : 'Arrastra aquí'}
                  </Typography>
                )}
              </Stack>
            </Box>
          );
        })}
      </Box>
    );
  };

  if (tipos === null) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;

  if (tipos.length === 0) {
    return (
      <Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Card sx={{ p: 3, textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Esta empresa no tiene tipos de orden</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Si haces trabajos antes de facturar (servicios, reparaciones, atenciones),
            crea un tipo de orden a partir de una plantilla.
          </Typography>
          <Button variant="outlined" onClick={() => navigate('/tipos-orden')}>Ir a Tipos de orden</Button>
        </Card>
      </Box>
    );
  }

  const encabezadoDato = campoId ? campoId.nombre : 'Detalle';

  return (
    <Box>
      <Box sx={ENCABEZADO}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {nombreSede} · el trabajo que se hace antes de facturar.
            Una orden terminada se convierte en factura.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} {...botonEncabezado(esMovil)} onClick={nueva}>
          {rotulo(esMovil, 'Nueva', `Nueva: ${tipo?.nombre || 'orden'}`)}
        </Button>
      </Box>

      {tipos.length > 1 && (
        <Tabs value={tipoId} onChange={(_, v) => { setTipoId(v); setBuscar(''); }}
          variant="scrollable" sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          {tipos.map(t => <Tab key={t.id} value={t.id} label={t.nombre} />)}
        </Tabs>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {aviso && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAviso('')}>{aviso}</Alert>}

      {resumen && (
        <Box sx={{ display: 'grid', gap: 1, mb: 1,
                   gridTemplateColumns: { xs: 'repeat(2, minmax(0,1fr))',
                                          sm: 'repeat(4, minmax(0,1fr))' } }}>
          {/* El dato principal es el conteo: lo que se pregunta en el
              mostrador es «cuántas van», y el monto acompaña. */}
          {[['En curso', resumen.en_curso, compacto(resumen.monto_en_curso), '#0369a1', '#e0f2fe'],
            ['Listas para facturar', resumen.listas_para_facturar, 'terminadas', '#15803d', '#dcfce7'],
            ['Facturadas', resumen.por_estado?.facturada?.cantidad || 0,
             compacto(resumen.por_estado?.facturada?.monto || 0), '#5b21b6', '#ede9fe'],
            ['Anuladas', resumen.por_estado?.anulada?.cantidad || 0, '—', '#b91c1c', '#fee2e2'],
          ].map(([t, v, sub, color, bg]) => (
            <Box key={t} sx={{ p: TARJETA.relleno, bgcolor: bg, borderRadius: TARJETA.radio,
                               minWidth: 0, overflow: 'hidden', display: 'grid', gap: 0.5 }}>
              <Typography sx={{ ...ETIQUETA_BLOQUE, color }}>{t}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, minWidth: 0 }}>
                <Typography sx={{ fontSize: { xs: 15, md: 14, xl: 15 }, fontWeight: 800, color,
                  lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>{v}</Typography>
                <Typography noWrap sx={{ fontSize: 9.5, fontWeight: 700, color, opacity: .75 }}>{sub}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Card>
        <CardContent sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <ToggleButtonGroup exclusive size="small" value={vista} onChange={(_, v) => cambiarVista(v)}>
            <ToggleButton value="tablero" sx={{ textTransform: 'none', px: 1.5 }}>
              <ViewKanbanIcon fontSize="small" sx={{ mr: 0.75 }} />Tablero
            </ToggleButton>
            <ToggleButton value="lista" sx={{ textTransform: 'none', px: 1.5 }}>
              <ViewListIcon fontSize="small" sx={{ mr: 0.75 }} />Lista
            </ToggleButton>
          </ToggleButtonGroup>
          {vista === 'lista' && (
          <TextField size="small" select label="Estado" value={estadoFiltro}
            onChange={e => setEstadoFiltro(e.target.value)} sx={filtro(190)}>
            <MenuItem value="abiertas">En curso</MenuItem>
            <MenuItem value="terminada">Listas para facturar</MenuItem>
            <MenuItem value="facturada">Facturadas</MenuItem>
            <MenuItem value="anulada">Anuladas</MenuItem>
            <MenuItem value="">Todas</MenuItem>
          </TextField>
          )}
          <TextField size="small" value={buscar}
            placeholder={campoId ? `${campoId.nombre}, número, cliente…` : 'Número, cliente…'}
            onChange={e => setBuscar(e.target.value)} sx={filtro(250)}
            InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 18, mr: 1, color: '#94a3b8' }} /> }} />
        </CardContent>
        <Divider />

        {loading && vista !== 'tablero' ? (
          <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
        ) : vista === 'tablero' ? (
          tablero()
        ) : ordenes.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">No hay órdenes con ese filtro.</Typography>
          </Box>
        ) : esMovil ? (
          /* En el teléfono cada orden es una ficha completa: la tabla obliga a
             arrastrar de lado para leer una sola. */
          <Box sx={{ p: TARJETA.relleno, display: 'grid', gap: 1 }}>
            {ordenes.map(o => {
              const r = resumenDatos(tipoDe(o), o.datos);
              return (
                <Paper key={o.id} elevation={0} sx={TARJETA_INTERACTIVA}>
                  <Stack direction="row" alignItems="flex-start" spacing={1}>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 800,
                                        fontFamily: r.principal ? 'ui-monospace, monospace' : undefined }}>
                        {r.principal || o.cliente}
                      </Typography>
                      {r.resto && (
                        <Typography noWrap sx={{ fontSize: 12, color: 'text.secondary' }}>{r.resto}</Typography>
                      )}
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      {chipPaso(o)}
                      <Typography sx={{ fontSize: 14, fontWeight: 800, mt: 0.5 }}>{fmt(o.total)}</Typography>
                    </Box>
                  </Stack>
                  <Box sx={{ mt: 0.5, display: 'grid', gap: 0.25 }}>
                    {r.principal && <Typography noWrap sx={{ fontSize: 13 }}>{o.cliente}</Typography>}
                    <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                      {o.numero} · {o.sede_codigo} · {fechaHora(o.fecha_ingreso)}
                      {o.responsable ? ` · ${o.responsable}` : ''}
                    </Typography>
                    {o.numero_factura && (
                      <Typography sx={{ fontSize: 11.5, color: 'primary.main', fontWeight: 700 }}>
                        {o.numero_factura}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ mt: 0.5, pt: 0.25, borderTop: '1px solid rgba(15,23,42,.08)' }}>
                    {acciones(o)}
                  </Box>
                </Paper>
              );
            })}
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={0}>
            <Table size="small" sx={tablaAncha(860)}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  {['Orden', 'Sede', encabezadoDato, 'Cliente', 'Ingreso', 'Paso', 'Total', ''].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11,
                      textTransform: 'uppercase', color: '#64748b' }}
                      align={h === 'Total' ? 'right' : 'left'}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {ordenes.map(o => {
                  const r = resumenDatos(tipoDe(o), o.datos);
                  return (
                    <TableRow key={o.id} hover>
                      <TableCell sx={{ fontWeight: 700, fontFamily: 'ui-monospace, monospace' }}>
                        {o.numero}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={o.sede_codigo} sx={{ height: 20, fontSize: 10.5 }} />
                      </TableCell>
                      <TableCell>
                        {r.principal && <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{r.principal}</Typography>}
                        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                          {r.resto || (r.principal ? '' : '—')}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: 12.5 }}>{o.cliente}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {fechaHora(o.fecha_ingreso)}
                        {o.responsable && (
                          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{o.responsable}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {chipPaso(o)}
                        {o.numero_factura && (
                          <Typography sx={{ fontSize: 10.5, color: 'text.secondary', mt: .3 }}>
                            {o.numero_factura}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {fmt(o.total)}
                      </TableCell>
                      <TableCell align="right">{acciones(o)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Alta y edición */}
      <Dialog fullScreen={esMovil} open={Boolean(edicion)} onClose={() => setEdicion(null)} fullWidth maxWidth="md">
        <DialogTitle>{edicion?.id ? `Editar ${edicion.numero}` : `Nueva: ${tipo?.nombre || 'orden'}`}</DialogTitle>
        <DialogContent dividers>
          {errorForm && <Alert severity="error" sx={{ mb: 2 }}>{errorForm}</Alert>}
          {edicion && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              {/* Con una sede activa no se pregunta: se hereda del menú. Solo
                  en «Todas las sedes» hay que decir de cuál es la orden. */}
              {(esTodas || edicion.id) && (
                <TextField select size="small" label="Sede *" fullWidth value={edicion.sede_id}
                  disabled={Boolean(edicion.id)}
                  helperText={edicion.id ? 'La sede no se cambia: el número ya salió de ella'
                    : 'Estás en «Todas las sedes»: escoge de cuál es'}
                  onChange={e => setEdicion({ ...edicion, sede_id: e.target.value })}>
                  {sedes.map(s => <MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>)}
                </TextField>
              )}

              {tipo && tipo.campos.length > 0 && (
                <Box sx={{ display: 'grid', gap: 2,
                           gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0,1fr))' } }}>
                  {[...tipo.campos].sort((a, b) => Number(b.identificador) - Number(a.identificador))
                    .map(campoFormulario)}
                </Box>
              )}

              {historial && (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  Vino {historial.visitas} vez{historial.visitas === 1 ? '' : 'es'}. Última:{' '}
                  <b>{historial.ordenes[0]?.numero}</b> el {fechaCorta(historial.ordenes[0]?.fecha)}
                  {historial.ordenes[0]?.servicios?.length
                    ? ` (${historial.ordenes[0].servicios.join(', ')})` : ''}.
                  Se completaron los datos que faltaban con los de esa vez.
                </Alert>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                {/* La orden sale a nombre de quien se factura, desde el
                    principio: descubrirlo en la caja es tarde. */}
                <SelectorCliente
                  terceros={terceros} value={edicion.tercero_id || null}
                  onCreado={cargarTerceros}
                  onChange={(id, t) => setEdicion({ ...edicion, tercero_id: id || '',
                    cliente_nombre: t ? t.razon_social : '',
                    cliente_telefono: (t && t.telefono) || edicion.cliente_telefono })} />
                <CampoTelefono etiqueta="Teléfono de contacto" fullWidth
                  valor={edicion.cliente_telefono || ''}
                  ayuda="A quién avisar cuando esté listo"
                  alCambiar={(v) => setEdicion({ ...edicion, cliente_telefono: v })} />
              </Stack>

              {tipo?.pide_responsable && (
                <TextField select size="small" label="Quién lo atiende" fullWidth
                  value={edicion.empleado_id || ''}
                  helperText={empleados.length ? undefined : 'No hay empleados: créalos en Empleados'}
                  onChange={e => {
                    const emp = empleados.find(x => x.id === e.target.value);
                    setEdicion({ ...edicion, empleado_id: e.target.value, responsable: emp ? emp.nombre : '' });
                  }}>
                  <MenuItem value="">Sin asignar</MenuItem>
                  {empleados.map(emp => (
                    <MenuItem key={emp.id} value={emp.id}>
                      {emp.nombre}
                      {emp.cargo && (
                        <Typography component="span" sx={{ fontSize: 11, color: 'text.secondary', ml: 1 }}>
                          {emp.cargo}
                        </Typography>
                      )}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              <Divider textAlign="left" sx={{ fontSize: 12, color: 'text.secondary' }}>
                {palabraLineas.charAt(0).toUpperCase() + palabraLineas.slice(1)}
              </Divider>
              {(edicion.lineas || []).map((l, i) => (
                <Stack key={i} direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap"
                  sx={{ '& > .MuiFormControl-root:first-of-type': { flexBasis: { xs: '100%', sm: 'auto' } } }}>
                  {/* Se escoge del catálogo y trae su precio, que queda
                      editable porque un descuento pasa. */}
                  <TextField select size="small" label="Ítem" sx={{ flexGrow: 1 }}
                    value={l.servicio_id || ''}
                    SelectProps={{ renderValue: (v) => {
                      const sv = vendibles.find(x => x.id === v);
                      return sv ? sv.nombre : (l.concepto || '');
                    } }}
                    onChange={e => {
                      const sv = vendibles.find(x => x.id === e.target.value);
                      const ls = [...edicion.lineas];
                      ls[i] = { ...l, servicio_id: e.target.value,
                        concepto: sv ? sv.nombre : l.concepto,
                        precio_unitario: sv ? sv.precio : l.precio_unitario };
                      setEdicion({ ...edicion, lineas: ls });
                    }}>
                    {vendibles.length === 0 && (
                      <MenuItem disabled value="">El catálogo está vacío</MenuItem>
                    )}
                    {vendibles.map(sv => (
                      <MenuItem key={sv.id} value={sv.id}>
                        <Box sx={{ display: 'flex', width: '100%', gap: 2, alignItems: 'baseline' }}>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography sx={{ fontSize: 13.5 }}>
                              {sv.nombre}
                              {sv.tipo === 'producto' && (
                                <Chip size="small" label="producto"
                                  sx={{ ml: 1, height: 18, fontSize: 10 }} />
                              )}
                            </Typography>
                            {sv.descripcion && (
                              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{sv.descripcion}</Typography>
                            )}
                          </Box>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>
                            {fmt(sv.precio)}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField size="small" label="Cant." type="number" sx={{ width: { xs: 84, sm: 90 } }}
                    value={l.cantidad ?? 1} onChange={e => {
                      const ls = [...edicion.lineas]; ls[i] = { ...l, cantidad: e.target.value };
                      setEdicion({ ...edicion, lineas: ls });
                    }} />
                  <TextField size="small" label="Precio" type="number"
                    sx={{ width: { xs: 'auto', sm: 140 }, flexGrow: { xs: 1, sm: 0 } }}
                    value={l.precio_unitario ?? ''} onChange={e => {
                      const ls = [...edicion.lineas]; ls[i] = { ...l, precio_unitario: e.target.value };
                      setEdicion({ ...edicion, lineas: ls });
                    }} />
                  <IconButton size="small" color="error" onClick={() => setEdicion({
                    ...edicion, lineas: edicion.lineas.filter((_, j) => j !== i) })}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              <Box>
                <Button size="small" startIcon={<AddIcon />} onClick={() => setEdicion({
                  ...edicion, lineas: [...(edicion.lineas || []), { ...LINEA_VACIA }] })}>
                  Agregar ítem
                </Button>
              </Box>

              <Box sx={{ textAlign: 'right', bgcolor: '#f8fafc', p: 1.5, borderRadius: 1 }}>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                  Subtotal {fmt(totalLineas(edicion.lineas))} · IVA {edicion.impuesto_pct}%
                </Typography>
                <Typography sx={{ fontSize: 19, fontWeight: 800 }}>
                  {fmt(totalLineas(edicion.lineas) * (1 + Number(edicion.impuesto_pct || 0) / 100))}
                </Typography>
              </Box>

              <TextField size="small" label="Observaciones" multiline rows={2} fullWidth
                value={edicion.observaciones || ''}
                onChange={e => setEdicion({ ...edicion, observaciones: e.target.value })} />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEdicion(null)}>Cancelar</Button>
          <Button variant="contained" onClick={guardar}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* Anulación: el motivo es obligatorio */}
      <Dialog fullScreen={esMovil} open={Boolean(anulando)} onClose={() => setAnulando(null)} fullWidth maxWidth="xs">
        <DialogTitle>Anular {anulando?.numero}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>
            Una orden anulada no se factura ni se edita. Di por qué, para que quede el rastro.
          </Typography>
          <TextField autoFocus fullWidth size="small" multiline rows={3} value={motivo}
            placeholder="El cliente no volvió, se abrió por error…"
            onChange={e => setMotivo(e.target.value)} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAnulando(null)}>Cancelar</Button>
          <Button variant="contained" color="error" disabled={!motivo.trim()}
            onClick={() => { moverA(anulando, { estado: 'anulada', motivo: motivo.trim() }); setAnulando(null); }}>
            Anular
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
